import { ClickEvent, DataSourceMode, DataSourceModeChangeEvent, Label, SelectionEvent, Snackbar, TableRowDisplayEvent, TableSelectionEvent } from "@mcleod/components";
import { DataSourceExecutionEvent } from "@mcleod/components/src/events/DataSourceExecutionEvent";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutMobileCommControlTabView } from "./autogen/AutogenLayoutMobileCommControlTabView";
import { Api, FileUtil, ModelRow, Navigation, PermissionsUtil, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { MobileCommControlUtils } from "./MobileCommControlUtils";

/**
 * Constants for tab identification and dialog configuration
 */
const TAB_CONSTANTS = {
    IDS: {
        CONTROL: "tabControl",
        FORMS: "tabForms",
        EXTERNAL_APPS: "tabExternalApps",
        SEND_LOAD_OPTIONS: "tabSendLoadOptions"
    },
    INDICES: {
        CONTROL: 0
    }
} as const;

/**
 * Dialog messages and configuration
 */
const DIALOG_CONFIG = {
    CONTROL_RECORD_REQUIRED: {
        message: "You cannot access this tab until the control record has been created. Please save the control record first.",
        title: "Control Record Required",
        options: { xVisible: false }
    }
} as const;

/**
 * Manages the mobile communication control tab view functionality.
 * Handles tab navigation, data source operations, and vendor information display.
 */
export class MobileCommControlTabView extends AutogenLayoutMobileCommControlTabView {
    private mobileCommButtons = null;
    private additionalButtons = [];
    private driverSyncVendors = [
        "CADEC", "DRVTECHW", "EA", "ERO", "FTS", "JJK", "KEEP", "MAVEN",
        "QCHOS", "QCP", "PEOP", "PLAT", "PED", "TELOGIS", "TND", "XATA",
        "XRS", "ZONARW"
    ];

    private locationSyncVendors = ["FLEET", "KEEP", "PED", "SKYBITZ", "XRS"];

    private vehicleSyncVendors = [
        "QCHOS", "XRS", "JJK", "KEEP"
    ];

    private loadFormVendors = [
        "KEEP", "PEOP", "MAVEN", "PLAT", "DRVTECHW", "OMNI", "PED", "RAND",
        "SAM", "IBRI", "IGLO", "TRAN"
    ];
    private requiresFileVendors = ["PLAT"];

    /**
     * Handles post-execution updates after mobile communication data source execution
     * @param event - The data source execution event
     */
    public sourceMobileCommAfterExecution(event: DataSourceExecutionEvent): void {
        this.updateVendorInformation();
        const vendorId = this.mainDataSource?.activeRow?.get("id");
        const isActive = this.mainDataSource?.activeRow?.get("is_active") === "Y";
        //    const enabled = layout?.mainDataSource?.mode === DataSourceMode.ADD;

        const portalURL = MobileCommControlUtils.getWebURL(this.mainDataSource?.activeRow?.get("id") || "");
        this.buttonPortal.enabled = isActive && !StringUtil.isEmptyString(vendorId) && !StringUtil.isEmptyString(portalURL) && !PermissionsUtil.isUserDeniedLayoutItem("lme/dispatch/mobilecomm/MobileCommControl", "buttonPortal");

        const hasDriverSyncVendor = vendorId && this.driverSyncVendors.some(syncVendor => vendorId.startsWith(syncVendor));
        this.buttonDrivers.enabled = isActive && hasDriverSyncVendor && !PermissionsUtil.isUserDeniedLayoutItem("lme/dispatch/mobilecomm/MobileCommControl", "buttonDrivers");

        const hasVehicleSyncVendor = vendorId && this.vehicleSyncVendors.some(syncVendor => vendorId.startsWith(syncVendor));
        this.buttonVehicles.enabled = isActive && hasVehicleSyncVendor && !PermissionsUtil.isUserDeniedLayoutItem("lme/dispatch/mobilecomm/MobileCommControl", "buttonVehicles");

        const hasLocationSyncVendor = vendorId && this.locationSyncVendors.some(syncVendor => vendorId.startsWith(syncVendor));
        this.buttonLocations.enabled = isActive && hasLocationSyncVendor && !PermissionsUtil.isUserDeniedLayoutItem("lme/dispatch/mobilecomm/MobileCommControl", "buttonLocations");

        const canLoadForms = vendorId && this.loadFormVendors.some(includeVendor => vendorId.startsWith(includeVendor));
        this.buttonForms.enabled = isActive && canLoadForms && !PermissionsUtil.isUserDeniedLayoutItem("lme/dispatch/mobilecomm/MobileCommControl", "buttonForms");
    }

    /**
     * Posts data to the mobile communication control data source
     * @returns Promise resolving to true if successful, false otherwise
     */
    public async postDataSources(): Promise<boolean> {
        try {
            await this.validateDataSourceAvailability();
            await this.layoutMobileCommControl.mainDataSource.post();
            return true;
        } catch (error) {
            console.error('Failed to post data sources:', error);
            return false;
        }
    }

    /**
     * Handles tab selection events and enforces business rules
     * @param event - The selection event containing tab selection information
     */
    public async tabsetBeforeTabSelection(event: SelectionEvent): Promise<void> {
        if (!this.isUserInitiatedChange(event)) {
            return;
        }

        const tabId = event.newSelection?.id;

        if (this.isRestrictedTab(tabId) && this.isAddMode()) {
            await this.handleRestrictedTabAccess(event);
        }
    }

    /**
     * Updates the vendor information display based on the active row data
     */
    private updateVendorInformation(): void {
        if (!this.isMainDataSourceAvailable()) {
            return;
        }

        try {
            const vendorName = this.layoutMobileCommControl.activeRow?.get("vendor_name");
            if (vendorName) {
                this.layoutMobileCommForms.labelVendorName.text = vendorName;
            }
        } catch (error) {
            console.error('Failed to update vendor information:', error);
        }
    }

    /**
     * Validates that required data sources are available
     * @throws Error if data sources are not available
     */
    private async validateDataSourceAvailability(): Promise<void> {
        if (!this.layoutMobileCommControl?.mainDataSource) {
            throw new Error('Mobile communication control data source is not available');
        }
    }

    /**
     * Checks if the change was initiated by user interaction
     * @param event - The selection event
     * @returns True if user initiated the change
     */
    private isUserInitiatedChange(event: SelectionEvent): boolean {
        return event.userInitiatedChange === true;
    }

    /**
     * Checks if the specified tab is restricted when in ADD mode
     * @param tabId - The tab identifier
     * @returns True if the tab is restricted
     */
    private isRestrictedTab(tabId: string | undefined): boolean {
        if (!tabId) {
            return false;
        }

        const restrictedTabs = [
            TAB_CONSTANTS.IDS.FORMS,
            TAB_CONSTANTS.IDS.EXTERNAL_APPS,
            TAB_CONSTANTS.IDS.SEND_LOAD_OPTIONS
        ] as const;

        return (restrictedTabs as readonly string[]).includes(tabId);
    }

    /**
     * Checks if the data source is in ADD mode
     * @returns True if in ADD mode
     */
    private isAddMode(): boolean {
        return this.layoutMobileCommControl?.mainDataSource?.mode === DataSourceMode.ADD;
    }

    /**
     * Checks if the main data source is available
     * @returns True if main data source is available
     */
    private isMainDataSourceAvailable(): boolean {
        return this.mainDataSource != null;
    }

    /**
     * Handles access to restricted tabs by showing a dialog and preventing navigation
     * @param event - The selection event to prevent
     */
    private async handleRestrictedTabAccess(event: SelectionEvent): Promise<void> {
        // Prevent tab selection
        event.preventDefault();

        // Show informative dialog
        await this.showControlRecordRequiredDialog();

        // Force user to stay on control tab
        this.resetToControlTab();
    }

    /**
     * Shows a dialog informing the user that a control record is required
     */
    private async showControlRecordRequiredDialog(): Promise<void> {
        const config = DIALOG_CONFIG.CONTROL_RECORD_REQUIRED;
        await CommonDialogs.showError(config.message, config.title );
    }

    /**
     * Resets the tab selection to the control tab
     */
    private resetToControlTab(): void {
        this.tabsetMobileCommControlTabView.selectedIndex = TAB_CONSTANTS.INDICES.CONTROL;
    }

    async tableFormGroupByOnSelect(event: TableSelectionEvent): Promise<void> {
    }

    tableFormGroupByAllOnRowDisplay(event: TableRowDisplayEvent): void {
        const tableRow = event.getTableRow();

        this.updateIsActiveLabel(tableRow);
//        this.setupFormTitleTooltip(tableRow);
    }

    /**
     * Updates the active status label with the appropriate icon.
     */
    private updateIsActiveLabel(tableRow: any): void {
        const labelIsActive = tableRow.findComponentById("labelIsActive") as Label;
        if (!labelIsActive) {
            return;
        }

        let isActive: string | boolean;
        if (tableRow.data instanceof ModelRow) {
            isActive = (tableRow.data as ModelRow)?.getBoolean("is_active", null);
        } else {
            isActive = tableRow.data["is_active"];
        }
        labelIsActive.text = "";
        labelIsActive.imageName = (isActive || isActive === "Y") ? ImageName.circleCheck : ImageName.circleCheckEmpty;
    }

    /**
     * Sets up the tooltip for the form title label.
     */
    /*
    private setupFormTitleTooltip(tableRow: any): void {
        const formTitleLabel = tableRow.findComponentById("labelFormTitle") as Label;
        if (!formTitleLabel) {
            return;
        }

        const vendorId = tableRow.data?.get("form_vendor", null);
        const formId = tableRow.data?.get("id", null);
        const formType = tableRow.data?.get("form_type", null);

        formTitleLabel.tooltipCallback = (baseTooltip, originatingEvent) => {
            const layout = Layout.getLayout("lme/dispatch/mobilecomm/MobileCommFormQuickInfo") as Layout;
            layout.addLayoutLoadListener(() => {
                layout.mainDataSource?.search({
                    id: formId,
                    mc_control_id: vendorId,
                    form_type: formType
                });
            });
            return formTitleLabel.showTooltip(layout, null, { themeKey: "quickInfo" });
        };
    }
    */

    /** This is an event handler for the onClick event of buttonPortal. */
    buttonPortalOnClick(event: ClickEvent) {
        this.buttonPortal.busy = true;
        const portalURL = MobileCommControlUtils.getWebURL(this.mainDataSource?.activeRow?.get("id") || "");
        if (!StringUtil.isEmptyString(portalURL)) {
            Navigation.navigateTo(portalURL, { newTab: true });
        }
        this.buttonPortal.busy = false;
    }

    /** This is an event handler for the onClick event of buttonDrivers. */
    async buttonDriversOnClick(event: ClickEvent) {
        this.buttonDrivers.busy = true;
        try {
            await Api.post("lme/dispatch/mobilecomm/mc-action", { action: "SYNC_DRIVERS", vendor: this.mainDataSource?.activeRow?.get("id") });
            Snackbar.showSuccessSnackbar("Driver sync completed successfully.");
        } catch (error) {
            Snackbar.showWarningSnackbar("Error initiating driver sync: " + error);
        }
        this.buttonDrivers.busy = false;
    }

    /** This is an event handler for the onClick event of buttonVehicles. */
    async buttonVehiclesOnClick(event: ClickEvent) {
        this.buttonVehicles.busy = true;
        try {
            await Api.post("lme/dispatch/mobilecomm/mc-action", { action: "SYNC_VEHICLES", vendor: this.mainDataSource?.activeRow?.get("id") });
            Snackbar.showSuccessSnackbar("Vehicle sync completed successfully.");
        } catch (error) {
            Snackbar.showWarningSnackbar("Error initiating vehicle sync: " + error);
        }
        this.buttonVehicles.busy = false;
    }

    /** This is an event handler for the onClick event of buttonTrailers. */
    buttonTrailersOnClick(event: ClickEvent) {

    }

    /** This is an event handler for the onClick event of buttonLocations. */
    async buttonLocationsOnClick(event: ClickEvent) {
        this.buttonLocations.busy = true;
        try {
            await Api.post("lme/dispatch/mobilecomm/mc-action", { action: "SYNC_LOCATIONS", vendor: this.mainDataSource?.activeRow?.get("id") });
            Snackbar.showSuccessSnackbar("Location sync completed successfully.");
        } catch (error) {
            Snackbar.showWarningSnackbar("Error initiating location sync: " + error);
        }
        this.buttonLocations.busy = false;
    }

    /** This is an event handler for the onClick event of buttonTerminals. */
    buttonTerminalsOnClick(event: ClickEvent) {

    }

    /** This is an event handler for the onClick event of buttonForms. */
    async buttonFormsOnClick(event: ClickEvent) {
        const vendorId = this.mainDataSource?.activeRow.get("id");
        this.buttonForms.busy = true;
        if (this.requiresFileVendors.some(vendor => vendorId.startsWith(vendor))) {
            FileUtil.showOpenFileDialog().then(file => {
                if (file) {
                    const reader = new FileReader();
                    reader.readAsText(file);
                    reader.onload = async () => {
                        const fileContent = reader.result as string;
                        try {
                            await Api.post("lme/dispatch/mobilecomm/mc-action", { action: "LOAD_FORMS", vendor: vendorId, form_content: fileContent });
                            Snackbar.showSuccessSnackbar("Finished API Macro Request.");
                        } catch (error) {
                            Snackbar.showWarningSnackbar("Error initiating Forward Load request: " + error);
                        }
                    }
                }
            });
        } else {
            if (await CommonDialogs.showYesNo("Begin API Return Form Request?", "Load Forms")) {
                try {
                    await Api.post("lme/dispatch/mobilecomm/mc-action", { action: "LOAD_FORMS", vendor: vendorId, form_content: "R"});
                    Snackbar.showSuccessSnackbar("Finished API Macro Request.");
                } catch (error) {
                    Snackbar.showWarningSnackbar("Error initiating Forward Load request: " + error);
                }
            }
        }
        this.buttonForms.busy = false;
    }

    /** This is an event handler for the afterModeChange event of sourceMCControl. */
    sourceMCControlAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode !== DataSourceMode.UPDATE) {
            this.panelTools.visible = false;
        }
    }
}
